import {useLocation, useParams} from "react-router-dom";
import {Navigation} from "@/components/Navigation";
import {Categories} from "@/components/Categories";
import {Footer} from "@/components/Footer";
import {Card} from "@/components/ui/card";
import {Button} from "@/components/ui/button";
import {Alert, AlertDescription} from "@/components/ui/alert";
import {
    Box,
    ChevronDown,
    Facebook,
    HeartHandshake,
    InfoIcon,
    Logs,
    MessageCircle,
    PartyPopper,
    Send,
    Share2,
    Smile,
    Ticket,
    Timer,
    Trophy,
    Twitter,
    Users
} from "lucide-react";
import {Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip,} from "recharts";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {Poll, PollStatus} from "@/types/Poll.ts";
import * as React from "react";
import {useEffect, useState} from "react";
import {toast} from "sonner";
import {Session} from "@supabase/supabase-js";
import {RecentVoters} from "@/components/RecentVoters";
import {Skeleton} from "@/components/ui/skeleton.tsx";
import {RelatedPolls} from "@/components/RelatedPolls";
import {PollStages} from "@/components/PollStages";
import {Comments} from "@/components/Comments";
import {useIsMobile} from "@/hooks/use-mobile";
import {Collapsible, CollapsibleContent, CollapsibleTrigger} from "@/components/ui/collapsible";
import {PollNavigation} from "@/components/PollNavigation";
import CountdownTimer from "@/components/CountdownTimer.tsx";
import {Head} from "@/components/Head.tsx";
import {Helmet} from "react-helmet-async";
import {SEO} from "@/components/SEO.tsx";
import BenefitsCard from "@/components/BenefitsCard";
import {useAuth} from "@/contexts/AuthContext.tsx";
import {useLoginModal} from "@/contexts/LoginModalContext.tsx";
import { ColorPairs } from "@/constants/colors";
import chroma from "chroma-js";
import TagsCard from "@/components/TagsCard";

const COLORS = ["#578FCA", "#A55B4B", "#EB5B00", "#FFB200", "#3D8D7A", "#B2A5FF", "#F4D793", "#E17564", "#A9C46C", "#66785F", "#FCE7C8", "#B1C29E", "#FADA7A", "#F0A04B", "#C0D6E8"];
export const ChartColors: string[] = ColorPairs.map((c) =>
    chroma(c.backgroundColor).darken(0.5).saturate(1.2).hex()
);
interface PollDetailInterface {
    poll: Poll,
    pollSummary: [
        {
            poll_id: string,
            poll_title: string,
            option_id: string,
            option_title: string,
            percentage: number,
        },
    ],
    prev?: Poll | null,
    next?: Poll | null,
}

const PollDetailSkeleton = () => (
    <>
        <Skeleton className="w-48 md:w-48 h-48 object-cover rounded-lg mb-4 md:mb-0 bg-gray-200"/>
        <div className="flex-1">
            <Skeleton className="h-20 w-full bg-gray-200"/>
            <div className="grid grid-cols-2 gap-4 mt-2">
                <Skeleton className="flex h-8 w-full bg-gray-200"/>
                <Skeleton className="flex h-8 w-full bg-gray-200"/>
                <Skeleton className="h-8 w-full bg-gray-200"/>
                <Skeleton className="h-8 w-full bg-gray-200"/>
            </div>

        </div>
    </>
);

const rules = [
    "Participation in polls is free",
    "You must be 16 years or older to participate",
    "One vote per person",
    "Votes cannot be changed once submitted",
    "The reward will be distributed within two weeks after the event",
    "Instant votes may be reflected late depending on site traffic",
    "The decision of the organizers is final"
];

const PollDetail = () => {
    const {slug} = useParams();
    const location = useLocation();
    const [selectedOption, setSelectedOption] = useState(null);
    const [handleButtonDisabled, setHandleButtonDisabled] = useState(false);
    /*const [userSession] = useState<Session | null>(() => {
        const cachedSession = localStorage.getItem('session');
        return cachedSession ? JSON.parse(cachedSession) : null;
    });*/

    const {user, session, loading: authLoading} = useAuth();
    const {openLoginModal} = useLoginModal();

    const [showShareModal, setShowShareModal] = useState(false);
    const [rulesOpen, setRulesOpen] = useState(false);
    const isMobile = useIsMobile();

    const queryClient = useQueryClient();

    const {data: pollDetail, isLoading} = useQuery({
        queryKey: ['polls', slug, session],
        queryFn: async () => {
            const url = `${import.meta.env.VITE_API_URL}/polls/${slug}`;
            let headers = {}
            // console.log("userSession", userSession);
            if(session?.access_token){
                headers = {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${session.access_token}`
                };
            }
            const response = await fetch(url, {
                method: 'GET',
                headers,
            });
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            const data: PollDetailInterface = await response.json();
            return data;
        },
        enabled: !!slug
    });

    const {data: userVote, isLoading: isUserVoteLoading, refetch: checkuserVoteRefetch} = useQuery({
        queryKey: ['userVote', pollDetail, session],
        queryFn: async () => {
            const url = `${import.meta.env.VITE_API_URL}/polls/vote/${pollDetail.poll.id}`;
            const headers = {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session.access_token}`
            };
            const response = await fetch(url, {
                method: 'GET',
                headers,
            });
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            const data = await response.json();
            // console.log("userVoteData:", data)
            return data;
        },
        enabled: !!pollDetail
    });

    const toggleSelectedOption = async (optionId: string) => {
        if (selectedOption === optionId) {
            setSelectedOption(null);
        } else {
            setSelectedOption(optionId);
        }
    }

    const handleSubmit = async () => {
        /*if (!userSession) {
            return toast.error('Please login to cast your vote');
        }*/
        if (!authLoading && (!user || !session)) {
            // console.log("hello");
            openLoginModal();
            return;
        }
        setHandleButtonDisabled(true);
        try {
            const url = `${import.meta.env.VITE_API_URL}/polls/vote`;
            const headers = {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session.access_token}`
            };
            const body = JSON.stringify({pollId: pollDetail.poll.id, optionId: selectedOption});

            const response = await fetch(url, {
                method: 'POST',
                headers,
                body,
            });

            if (!response.ok) {
                return toast.error('An error occurred while saving vote!');
            }

            // await queryClient.invalidateQueries({queryKey: ['userVote', pollDetail]});
            // await queryClient.invalidateQueries({queryKey: ['polls', slug]});
            await checkuserVoteRefetch();
            return toast.success('Your vote has been saved!');
        } catch (error) {
            // console.error('Error:', error);
        }
        setHandleButtonDisabled(false);
    }

    const handleShareToSocial = (platform: string) => {
        let shareUrl = '';
        const pollTitle = pollDetail?.poll?.title || "Check out this poll!";
        const inviteText = "Join me and vote in this interesting poll on BlankPoll! Your opinion matters.";
        const currentUrl = window.location.href;

        switch (platform) {
            case 'facebook':
                shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentUrl)}&quote=${encodeURIComponent(`${pollTitle} - ${inviteText}`)}`;
                break;
            case 'twitter':
                shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(`${pollTitle} - ${inviteText}`)}&url=${encodeURIComponent(currentUrl)}`;
                break;
            case 'reddit':
                shareUrl = `https://www.reddit.com/submit?url=${encodeURIComponent(currentUrl)}&title=${encodeURIComponent(pollTitle)}`;
                break;
            case 'telegram':
                shareUrl = `https://t.me/share/url?url=${encodeURIComponent(currentUrl)}&text=${encodeURIComponent(`${pollTitle} - ${inviteText}`)}`;
                break;
            case 'discord':
                navigator.clipboard.writeText(`${pollTitle} - ${inviteText}\n${currentUrl}`);
                toast.success('Discord link copied to clipboard! Paste it in your Discord chat.');
                toast(
                    <div className="flex items-center gap-2">
                        <PartyPopper className="h-5 w-5 text-green-500"/>
                        <span>You earned 300 bp points for sharing!</span>
                    </div>,
                    {duration: 5000}
                );
                setShowShareModal(false);
                return;
            default:
                return;
        }

        window.open(shareUrl, '_blank', 'width=600,height=400');
        toast(
            <div className="flex items-center gap-2">
                <PartyPopper className="h-5 w-5 text-green-500"/>
                <span>You earned 300 bp points for sharing!</span>
            </div>,
            {duration: 5000}
        );
        setShowShareModal(false);
    };

    useEffect(() => {
        if (pollDetail) {
            const randomVoters = Math.floor(Math.random() * (308 - 110 + 1)) + 110;
            const isPollActive = pollDetail.poll.status && new Date(pollDetail.poll.endDate) > new Date(Date.now());

            toast(
                <div className="flex items-center gap-2">
                    <Smile className="h-5 w-5"/>
                    <span>
                        {isPollActive
                            ? `${randomVoters} people are voting on this poll right now`
                            : `${randomVoters} people are here`}
                    </span>
                </div>,
                {
                    duration: 5500,
                }
            );
        }
    }, [pollDetail]);

    useEffect(() => {
        // console.log(slug)
    }, [slug]);

    const SponsorThisPollCard = () => (
        <Card className="p-6 mb-8 border-0 overflow-hidden relative group cursor-pointer"
              style={{
                  background: "#ECF6D8",
              }}
              onClick={() => window.location.href = '/business'}
        >
            <div
                className="absolute inset-0 bg-gradient-to-r from-[#ECF6D8] to-[#ECF6D8] opacity-100 transition-all duration-300"></div>
            <div className="relative z-10 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                    <div className="bg-white/20 p-2 rounded-full">
                        <HeartHandshake className="h-6 w-6 text-[#344F16]"/>
                    </div>
                    <div>
                        <h3 className="text-xl font-bold text-[#344F16]">Sponsor This Poll</h3>
                        <p className="text-[#344F16] text-sm">Increase brand visibility with your target audience</p>
                    </div>
                </div>
                <Button
                    variant="outline"
                    className="bg-white text-[#344F16] border-white hover:bg-white/90 dark:bg-white dark:text-[#344F16] dark:hover:bg-white/90 dark:border-white text-xs py-1 px-3 h-8"
                >
                    Learn More
                </Button>
            </div>
        </Card>
    );

    const BoostShareCard = () => (
        <Card
            className="p-6 mb-4 border shadow-md dark:border-gray-700 overflow-hidden relative dark:bg-gradient-to-br dark:from-gray-800/80 dark:to-gray-700/50 dark:shadow-md"
            style={{
                backgroundImage: `linear-gradient(
                    45deg,
                    hsl(20deg 23% 97%) 0%,
                    hsl(22deg 84% 96%) 8%,
                    hsl(25deg 100% 95%) 17%,
                    hsl(29deg 100% 93%) 25%,
                    hsl(31deg 100% 91%) 33%,
                    hsl(33deg 100% 89%) 42%,
                    hsl(35deg 100% 87%) 50%,
                    hsl(37deg 98% 85%) 58%,
                    hsl(40deg 91% 83%) 67%,
                    hsl(43deg 84% 80%) 75%,
                    hsl(47deg 76% 77%) 83%,
                    hsl(50deg 69% 74%) 92%,
                    hsl(55deg 63% 71%) 100%
                )`,
                backgroundSize: 'cover'
            }}
        >
            <div className="flex flex-col md:flex-row items-center justify-between">
                <div className="flex items-center space-x-3 mb-4 md:mb-0">
                    <div className="bg-amber-100 dark:bg-amber-900/30 p-2 rounded-full">
                        <Share2 className="h-6 w-6 text-amber-600 dark:text-[#141821]"/>
                    </div>
                    <div>
                        <h3 className="text-xl font-bold dark:text-[#141821]">Boost!</h3>
                        <p className="text-gray-600 dark:text-[#141821] text-sm">Share this poll on social media and
                            earn 300 bp points</p>
                    </div>
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    className="bg-white hover:bg-white/90 text-gray-800 border-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 dark:border-gray-600"
                    onClick={() => setShowShareModal(true)}
                >
                    <Share2 className="h-4 w-4 mr-1"/>
                    Share
                </Button>
            </div>
        </Card>
    );

    const ShareModal = () => {
        if (!showShareModal) return null;

        return (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full animate-fade-in">
                    <div className="p-6">
                        <h3 className="text-xl font-bold mb-4 text-center dark:text-white">Share This Poll</h3>
                        <p className="text-gray-600 dark:text-gray-300 text-center mb-6">Share with your friends and
                            earn 300 bp points for each platform you share on!</p>

                        <div className="grid grid-cols-2 gap-4 mb-6">
                            <Button
                                className="bg-[#1877F2] hover:bg-[#1877F2]/90 text-white border-[#1877F2] dark:bg-[#1877F2] dark:text-white dark:hover:bg-[#1877F2]/90 dark:border-[#1877F2]"
                                onClick={() => handleShareToSocial('facebook')}
                            >
                                <Facebook className="h-4 w-4 mr-2"/>
                                Facebook
                            </Button>
                            <Button
                                className="bg-[#1DA1F2] hover:bg-[#1DA1F2]/90 text-white border-[#1DA1F2] dark:bg-[#1DA1F2] dark:text-white dark:hover:bg-[#1DA1F2]/90 dark:border-[#1DA1F2]"
                                onClick={() => handleShareToSocial('twitter')}
                            >
                                <Twitter className="h-4 w-4 mr-2"/>
                                Twitter
                            </Button>
                            <Button
                                className="bg-[#FF4500] hover:bg-[#FF4500]/90 text-white border-[#FF4500] dark:bg-[#FF4500] dark:text-white dark:hover:bg-[#FF4500]/90 dark:border-[#FF4500]"
                                onClick={() => handleShareToSocial('reddit')}
                            >
                                <MessageCircle className="h-4 w-4 mr-2"/>
                                Reddit
                            </Button>
                            <Button
                                className="bg-[#0088CC] hover:bg-[#0088CC]/90 text-white border-[#0088CC] dark:bg-[#0088CC] dark:text-white dark:hover:bg-[#0088CC]/90 dark:border-[#0088CC]"
                                onClick={() => handleShareToSocial('telegram')}
                            >
                                <Send className="h-4 w-4 mr-2"/>
                                Telegram
                            </Button>
                            <Button
                                className="bg-[#5865F2] hover:bg-[#5865F2]/90 text-white border-[#5865F2] dark:bg-[#5865F2] dark:text-white dark:hover:bg-[#5865F2]/90 dark:border-[#5865F2] col-span-2"
                                onClick={() => handleShareToSocial('discord')}
                            >
                                <MessageCircle className="h-4 w-4 mr-2"/>
                                Discord
                            </Button>
                        </div>

                        <div className="flex justify-center">
                            <Button
                                variant="outline"
                                onClick={() => setShowShareModal(false)}
                                className="dark:text-white dark:border-gray-600"
                            >
                                Cancel
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const CastYourVoteCard = () => (
        <Card className="p-6 dark:bg-gray-800/50 dark:border-gray-700">
            {isLoading || authLoading ? (
                <>
                    <Skeleton
                        className="mb-4 h-10 w-1/2 bg-gray-200"
                    />
                    <div className="flex flex-col items-center justify-center w-full space-y-3 py-2">
                    <Skeleton
                        className="mb-4 h-36 w-full bg-gray-200 p-2 rounded-xl p-2"
                    />
                    </div>
                </>
            ) : (
                (userVote?.userVote) ? (
                    <>
                        <h2 className="text-xl font-semibold mb-4 dark:text-white">Your Vote</h2>
                        <Alert className="mt-4 dark:bg-gray-700/50 dark:border-gray-600">
                            <div className="flex flex-col items-center justify-center w-full space-y-3 py-2">
                                <div className="rounded-full bg-green-100 dark:bg-green-900/30 p-2">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="text-green-600 dark:text-green-400"
                                    >
                                        <polyline points="20 6 9 17 4 12"/>
                                    </svg>
                                </div>
                                <AlertDescription className="text-center dark:text-gray-300">
                                    <span className="block mb-2">You voted for:</span>
                                    <strong className="text-lg dark:text-white">
                                        {pollDetail?.poll?.options?.find((option) => option.id === userVote?.userVote?.option_id)?.title}
                                    </strong>
                                </AlertDescription>
                            </div>
                        </Alert>
                    </>
                ) : (new Date(pollDetail?.poll?.endDate) > new Date(Date.now()) && pollDetail?.poll?.status) ? (
                    <>
                        <h2 className="text-xl font-semibold mb-4 dark:text-white">Cast Your Vote</h2>
                        <div className="space-y-3">
                            {pollDetail?.poll?.options?.map((option, index) => (
                                    <Button
                                        key={option.id}
                                        variant={selectedOption === option.id ? "default" : "outline"}
                                        className={`w-full justify-start ${
                                            selectedOption === option.id
                                                ? "dark:bg-[#2C3930] dark:text-white dark:hover:bg-[#2C3930]/90"
                                                : "dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                                        }`}
                                        onClick={(e) => toggleSelectedOption(option.id)}
                                    >
                                        {option?.title}
                                    </Button>
                                )
                            )}
                        </div>
                        <Button
                            className="w-full mt-6 bg-[#96A58D] hover:bg-[#96A58D]/90 text-white dark:bg-[#2C3930] dark:hover:bg-[#2C3930]/90"
                            disabled={!selectedOption || handleButtonDisabled}
                            onClick={() => handleSubmit()}
                        >
                            Submit Vote
                        </Button>
                        <Alert className="mt-4 dark:bg-gray-700/50 dark:border-gray-600">
                            <AlertDescription className="dark:text-gray-300">
                                You can only vote once. Make sure to choose carefully!
                            </AlertDescription>
                        </Alert>
                    </>
                ) : (
                    <>
                        <h2 className="text-xl font-semibold mb-4 dark:text-white">Cast Your Vote</h2>
                        <Alert className="mt-4 dark:bg-gray-700/50 dark:border-gray-600">
                            <div className="flex flex-col items-center justify-center w-full space-y-3 py-2">
                                <div className="rounded-full bg-red-100 dark:bg-red-900/30 p-2">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="text-red-600 dark:text-red-400"
                                    >
                                        <line x1="18" y1="6" x2="6" y2="18"/>
                                        <line x1="6" y1="6" x2="18" y2="18"/>
                                    </svg>
                                </div>
                                <AlertDescription className="text-center dark:text-gray-300">
                                    <strong className="text-lg dark:text-white">
                                        Poll Ended
                                    </strong>
                                </AlertDescription>
                            </div>
                        </Alert>
                    </>
                )
            )}
        </Card>
    );

    return (

        <div className="min-h-screen bg-white dark:bg-background font-sans">
            <SEO
                title={pollDetail?.poll?.title || "blankpoll"}
                description="View detailed information and results for this poll"
                type="website"
                name="blankpoll"
                imageUrl="/blankpoll-banner.jpg"
            />
            {/*{pollDetail && pollDetail?.poll && (
                <SEO title={pollDetail.poll.title}
                     description={"Vote, Play, Discover. Your Opinion Shapes the Future."}
                imageUrl={pollDetail.poll.imageUrl}
                name={"blankpoll"}
                type={"summary_large_image"}/>
                // <Helmet>
                //     <>
                //     <meta property="og:title" content={`${pollDetail.poll.title}`}/>
                //     <meta property="og:description"
                //           content="Vote, Play, Discover. Your Opinion Shapes the Future."/>
                //     <meta property="og:image" content={pollDetail.poll.imageUrl}/>
                //     <meta property="og:url" content={`https://blankpoll.com/polls/${slug}`}/>
                //     <meta property="og:type" content="website"/>
                //
                //     <meta name="twitter:card" content="summary_large_image"/>
                //     <meta name="twitter:title" content={`${pollDetail.poll.title}`}/>
                //     <meta name="twitter:description"
                //           content="Vote, Play, Discover. Your Opinion Shapes the Future."/>
                //     <meta name="twitter:image" content={pollDetail.poll.imageUrl}/>
                //     </>
                // </Helmet>
            )}*/}
            {/*{(pollDetail && pollDetail?.poll) && (
                <Head
                    title={pollDetail?.poll?.title}
                    description={"Vote, Play, Discover. Your Opinion Shapes the Future. Turn Opinions into Impact"}
                    ogImage={pollDetail?.poll?.imageUrl}
                    url={`${import.meta.env.VITE_SITE_URL}/polls/${pollDetail?.poll?.slug}`}
                />
            )}*/}

            <Navigation/>
            <Categories/>

            <main className="max-w-7xl mx-auto px-4 py-12 mt-12 relative">
                {!isLoading && !isMobile && <PollNavigation prevPoll={pollDetail?.prev} nextPoll={pollDetail?.next}/>}

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    <div className="lg:col-span-2">
                        <div className="flex flex-col md:flex-row md:items-start md:gap-6 mb-8">
                            {isLoading ? (
                                <PollDetailSkeleton/>
                            ) : (
                                <>
                                    <img
                                        src={pollDetail?.poll?.imageUrl}
                                        alt={pollDetail?.poll?.title}
                                        className="w-full md:w-48 h-48 object-cover rounded-lg mb-4 md:mb-0"
                                    />
                                    <div className="flex-1">
                                        <h1 className="text-2xl md:text-3xl text-justify font-bold text-secondary dark:text-white mb-4">
                                            {pollDetail?.poll?.title}
                                        </h1>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="flex items-center text-gray-600 dark:text-gray-300">
                                                <Logs className="w-5 h-5 mr-2 shrink-0"/>
                                                <span className="truncate">
                                                {pollDetail?.poll?.categories?.slug.slice(0, 1).toUpperCase() + pollDetail?.poll?.categories?.slug.slice(1)}
                                                </span>
                                                    {/*<Users className="w-5 h-5 mr-2 shrink-0"/>
                                                <span
                                                    className="truncate">{pollDetail?.poll?.totalVotes} total votes</span>*/}
                                            </div>
                                            <div className="flex items-center text-gray-600 dark:text-gray-300">
                                                <Trophy className="w-5 h-5 mr-2 shrink-0"/>
                                                <span className="truncate">Prize: {pollDetail?.poll?.prize} bp</span>
                                            </div>
                                            <div className="flex items-center text-gray-600 dark:text-gray-300">
                                                <Timer className="w-5 h-5 mr-2 shrink-0"/>
                                                <span className="truncate">
                                                    {(pollDetail?.poll?.status == PollStatus.active && new Date(pollDetail?.poll?.endDate) > new Date(Date.now())) ? (
                                                        `Ends on: ${new Date(pollDetail?.poll?.endDate).toLocaleString('tr-TR', {
                                                            day: '2-digit',
                                                            month: '2-digit',
                                                            year: 'numeric',
                                                            hour: '2-digit',
                                                            minute: '2-digit',
                                                            hour12: false
                                                        })}`
                                                        // <CountdownTimer endDate={new Date(pollDetail?.poll?.endDate).toISOString()} displayStyle="text" inDetail={true}/>
                                                    ) :
                                                        (pollDetail?.poll?.resolvedAt) ? (`Resolved: ${new Date(pollDetail?.poll?.resolvedAt).toLocaleString('tr-TR', {
                                                                day: '2-digit',
                                                                month: '2-digit',
                                                                year: 'numeric',
                                                                hour: '2-digit',
                                                                minute: '2-digit',
                                                                hour12: false
                                                            })}`)
                                                        : (
                                                        'Time is up'
                                                        /*`Ended On: ${new Date(pollDetail?.poll?.endDate).toLocaleString('tr-TR', {
                                                            day: '2-digit',
                                                            month: '2-digit',
                                                            year: 'numeric',
                                                            hour: '2-digit',
                                                            minute: '2-digit',
                                                            hour12: false
                                                        })}`*/
                                                    )}
                                                </span>
                                            </div>
                                            <div className="flex items-center text-gray-600 dark:text-gray-300">
                                                <Ticket className="w-5 h-5 mr-2 shrink-0"/>
                                                <span className="truncate">
                                                    {(pollDetail?.poll?.status && new Date(pollDetail?.poll?.endDate) > new Date(Date.now()))
                                                        ? "Active Poll" : "Poll Ended"}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>

                        <div className="block lg:hidden mb-8">
                            {/*<BoostShareCard/>*/}
                            <div>
                                <RecentVoters pollTitle={pollDetail?.poll?.title || ""}/>
                            </div>
                            <div className="mt-4">
                                <CastYourVoteCard/>
                            </div>
                            <div className="mt-4">
                                <BenefitsCard pollDetail={pollDetail}/>
                            </div>
                        </div>

                        <Card className="p-6 mb-8 dark:bg-gray-800/50 dark:border-gray-700">
                            <h2 className="text-xl font-semibold mb-4 dark:text-white">Current Results</h2>
                            <div className="h-[310px] sm:h-400px] md:h-[400px] flex items-center justify-center">
                                {pollDetail?.poll?.totalVotes === 0 ? (
                                    <div className="text-center text-gray-500 dark:text-gray-400">
                                        <p>No votes have been cast yet.</p>
                                        <p className="mt-2">Be the first to vote!</p>
                                    </div>
                                ) : (
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={pollDetail?.poll?.options?.map((detail) => {

                                                    return {
                                                        name: detail.title,
                                                        value: parseFloat(
                                                            pollDetail?.poll?.summary.find((x) => x.optionId == detail.id).percentage?.toFixed(2) || "0",
                                                            // (detail.vote_count / pollDetail?.poll?.totalVotes * 100).toFixed(2)
                                                        ),
                                                    };
                                                })
                                                    // .filter(detail => detail.value > 0)
                                            }
                                                cx="50%"
                                                cy="50%"
                                                labelLine={false}
                                                label={false}
                                                outerRadius={window.innerWidth < 640 ? 90 : 150}
                                                fill="#8884d8"
                                                dataKey="value"
                                                fillOpacity={1}
                                                opacity={1}
                                            >
                                                {pollDetail?.poll?.options?.map((entry, index) => (
                                                    <Cell
                                                        key={`cell-${index}`}
                                                        // fill={ColorPairs[index % ColorPairs.length].backgroundColor}
                                                        fill={ChartColors[index % ChartColors.length]}
                                                        fillOpacity={1}
                                                        opacity={1}
                                                    />
                                                ))}
                                            </Pie>
                                            <Tooltip
                                                formatter={(value) => `${value}%`}
                                                contentStyle={{
                                                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                                    border: '1px solid #ccc',
                                                    borderRadius: '4px',
                                                    color: '#000',
                                                    fontSize: window.innerWidth < 640 ? '10px' : '14px'
                                                }}
                                                itemStyle={{
                                                    color: '#000',
                                                    fontSize: window.innerWidth < 640 ? '10px' : '14px'
                                                }}
                                                labelStyle={{
                                                    color: '#000',
                                                    fontSize: window.innerWidth < 640 ? '10px' : '14px'
                                                }}
                                            />
                                            <Legend
                                                payload={pollDetail?.poll?.options?.map((detail, index) => ({
                                                    id: detail.id,
                                                    type: "square",
                                                    value: detail.title,
                                                    color: ChartColors[index % ChartColors.length],
                                                    // color: ColorPairs[index % ColorPairs.length].backgroundColor,
                                                }))}
                                                wrapperStyle={{
                                                    fontSize: window.innerWidth < 640 ? '10px' : '14px'
                                                }}
                                            />
                                        </PieChart>
                                    </ResponsiveContainer>
                                )}
                            </div>
                        </Card>

                        {(pollDetail?.poll?.id && <Comments pollId={pollDetail?.poll?.id}/>)}

                        <Card className="p-6 mb-8 dark:bg-gray-800/50 dark:border-gray-700">
                            <h2 className="text-xl font-semibold mb-4 dark:text-white">Hint</h2>
                            <div className="flex items-start gap-2 text-justify">
                                {pollDetail?.poll?.hint ? (
                                    <>
                                        <Box className="w-5 h-5 mt-0.5 text-primary dark:text-gray-300"/>
                                        <span className="dark:text-gray-300">{pollDetail.poll.hint}</span>
                                    </>
                                ) : (
                                    <>
                                        <Smile className="w-5 h-5 mt-0.5 text-primary dark:text-gray-300"/>
                                        <span className="dark:text-gray-300">No hints available for this poll.</span>
                                    </>
                                )}
                            </div>
                        </Card>

                        <Card className="p-6 mb-8 dark:bg-gray-800/50 dark:border-gray-700">
                            <Collapsible
                                open={rulesOpen}
                                onOpenChange={setRulesOpen}
                                className="w-full"
                            >
                                <CollapsibleTrigger className="flex items-center justify-between w-full text-left">
                                    <h2 className="text-xl font-semibold dark:text-white">Rules</h2>
                                    <ChevronDown className={`h-5 w-5 transition-transform duration-200 ${rulesOpen ? 'rotate-180' : ''}`} />
                                </CollapsibleTrigger>
                                <CollapsibleContent className="mt-4">
                                    <ul className="space-y-2 text-justify">
                                        {rules.map((rule, index) => (
                                            <li key={index} className="flex items-start gap-2">
                                                <InfoIcon className="w-5 h-5 mt-0.5 text-primary dark:text-gray-300"/>
                                                <span className="dark:text-gray-300">{rule}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </CollapsibleContent>
                            </Collapsible>
                        </Card>

                        {isMobile && pollDetail?.poll && (
                            <div className="mb-8">
                                <PollStages status={pollDetail.poll.status} />
                            </div>
                        )}

                        {/*{isMobile && (
                            <div className="mb-8">
                                <TagsCard />
                            </div>
                        )}*/}

                        {pollDetail && (
                            <RelatedPolls
                                currentPollId={pollDetail.poll.id}
                                categorySlug={pollDetail.poll.categories?.slug}
                            />
                        )}

                        <SponsorThisPollCard/>

                        {!isLoading && isMobile && (
                            <div className="mb-8">
                                <PollNavigation prevPoll={pollDetail?.prev} nextPoll={pollDetail?.next}/>
                            </div>
                        )}
                    </div>

                    <div className="hidden lg:block lg:col-span-1">
                        <div className="sticky top-36">
                            {/*<BoostShareCard/>*/}
                            <div>
                                <RecentVoters pollTitle={pollDetail?.poll?.title || ""}/>
                            </div>
                            <div className="mt-6">
                                <CastYourVoteCard/>
                            </div>
                            <div className="mt-6">
                                <BenefitsCard pollDetail={pollDetail}/>
                            </div>
                            {/*<div className="mt-6">
                                <TagsCard />
                            </div>*/}
                            {pollDetail?.poll && (
                                <div className="mt-6">
                                    <PollStages status={pollDetail.poll.status} />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </main>

            <ShareModal/>
            <Footer/>
        </div>
    );
};

export default PollDetail;
